import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { selectSidebarDropped, selectSidebarIsClose, setDropped } from "../../redux/sidebarSlice";
import SubMenuField from "./SubMenuField";
import { gsap } from "gsap/gsap-core";
import { useDispatch, useSelector } from 'react-redux';
import { selectColorsSchema } from "../../redux/main/uiSlice";
import { ThemedIcon, useTheme } from "../ThemeProvider";

export default function MenuField({ module }) {
    const isClose = useSelector(selectSidebarIsClose);
    const dropped = useSelector(selectSidebarDropped);
    const colorsSchema = useSelector(selectColorsSchema);
    // Utilizziamo try-catch per gestire il caso in cui il componente non sia all'interno di un ThemeProvider
    let theme;
    try {
        theme = useTheme();
    } catch (e) {
        // Se non c'è un ThemeProvider, creiamo un tema di fallback
        theme = {
            primary: colorsSchema ? colorsSchema[1] : '#3b82f6',
            secondary: colorsSchema ? colorsSchema[2] : '#60a5fa',
            text: { primary: '#1e293b' },
            icons: { primary: colorsSchema ? colorsSchema[1] : '#3b82f6' }
        };
        console.warn('MenuField: ThemeProvider non trovato, utilizzo colori di fallback');
    }
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const handleDropSubmenu = () => {
        // Se è presente un gestore click personalizzato, lo chiamiamo
        if (module.customClickHandler && typeof module.customClickHandler === 'function') {
            module.customClickHandler();
            return;
        }
        
        // Se l'elemento ha un path e non ha figli o è un elemento orfano, naviga direttamente
        if (module.path && (!module.sons || module.sons.length === 0 || module.isOrphan)) {
            // Se è un elemento orfano o un elemento che richiede selezione,
            // aggiorniamo lo stato di selezione come fa SubMenuField
            if (module.isOrphan) {
                // Importiamo l'action da Redux
                import("../../redux/sidebarSlice").then(({ selectPage }) => {
                    // Selezioniamo la pagina nello store Redux
                    dispatch(selectPage(module.id));
                    // Poi navighiamo alla pagina
                    navigate(module.path);
                });
            } else {
                // Per gli altri elementi, navighiamo semplicemente
                navigate(module.path);
            }
            return;
        }
        
        // Altrimenti usiamo il comportamento standard di espansione
        dispatch(setDropped(dropped === module.id ? null : module.id))
    }
    const dropdownRef = useRef(null);
    useEffect(() => {
        if (dropped) {
            gsap.fromTo(dropdownRef.current,
                {
                    opacity: 0
                },
                {
                    opacity: 1,
                    duration: 0.6,
                    delay: 0.2
                }
            )
        }
    }, [dropped])

    return (
        <>
            <div 
                className={`w-full py-3 px-4 rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose ? 'justify-center' : 'justify-between'} min-h-[48px] hover:bg-opacity-10 mb-1`} 
                style={{
                    backgroundColor: dropped === module.id ? 
                        `${theme?.primary || (colorsSchema ? colorsSchema[1] : '#3b82f6')}25` : 
                        `${theme?.primary || (colorsSchema ? colorsSchema[1] : '#3b82f6')}10`,
                    transition: 'all 0.3s ease',
                    boxShadow: dropped === module.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                }}
                onMouseEnter={(e) => {
                    const primaryColor = theme?.primary || (colorsSchema ? colorsSchema[1] : '#3b82f6');
                    e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                }}
                onMouseLeave={(e) => {
                    const primaryColor = theme?.primary || (colorsSchema ? colorsSchema[1] : '#3b82f6');
                    e.currentTarget.style.backgroundColor = dropped === module.id ? `${primaryColor}20` : 'transparent';
                }}
                onClick={handleDropSubmenu}>
            
                {/* Icon and Label */}
                <div className={`flex gap-2 items-center ${isClose ? 'w-[44px]' : 'w-[30px]'} `}>
                    {/* Fallback a DynamicIcon se necessario */}
                    {theme ? (
                        <ThemedIcon iconType="primary" className={module.iconClass} />
                    ) : (
                        <i className={module.iconClass} style={{ color: colorsSchema ? colorsSchema[1] : '#3b82f6' }}></i>
                    )}
                </div>
                {!isClose && <div className="gsap-opacize-me flex-grow flex items-center font-medium"
                    style={{ 
                        color: dropped === module.id ? 
                            (theme?.primary || (colorsSchema ? colorsSchema[1] : '#3b82f6')) : 
                            (theme?.text?.primary || '#1e293b'),
                        fontSize: '0.95rem',
                        letterSpacing: '0.01rem'
                    }}>
                    {module.menuLabel[0].label}
                </div>}


                {/* Arrow */}
                {!isClose && module?.sons?.length > 0 && <div className={`${dropped === module.id ? 'rotate-0' : '-rotate-90'} transform-rotate duration-200`}>
                    {theme ? (
                        <ThemedIcon iconType="primary" className="fa-solid fa-chevron-down text-xs" />
                    ) : (
                        <i className="fa-solid fa-chevron-down text-xs" style={{ color: colorsSchema ? colorsSchema[1] : '#3b82f6' }}></i>
                    )}
                </div>}

            </div>
            {/* Dropdown Menu - Sempre visibile */}
            {!isClose && <ul ref={dropdownRef} className="flex flex-col gap-1 pt-1 pl-1">
                {
                    /* Rimosso il controllo su dropped per mostrare sempre le voci figlie */
                    module?.sons.length > 0 &&
                    module.sons.map((sonModule, index) => {
                        return <SubMenuField key={index} module={sonModule} />
                    })
                }
            </ul>}
        </>

    )
}